import { z } from "zod"

import {
  CUSTOMISATION_CATEGORIES,
  SETTINGS_SCOPE,
} from "@tfg/types/constants/communication-studio.constant"
import { PRODUCT_ATTRIBUTES } from "@tfg/types/constants/configurations.constants"
import { FormFileSchema } from "@tfg/types/schemas/file"
import { ConfigurationsScopeType } from "@tfg/types/types/communication-studio/configurations.types"

export const FooterComponentCustomisationConfigurationSchema = z.object({
  showLastUpdateTimestamp: z.boolean(),
  showPoweredByTFG: z.boolean(),
})
export type FooterComponentCustomisationConfiguration = z.infer<
  typeof FooterComponentCustomisationConfigurationSchema
>

// Category configuraitons
export const MainHeaderComponentCustomisationConfigurationSchema = z.object({
  showProductImage: z.boolean(),
  showProductReference: z.boolean(),
})
export type MainHeaderComponentCustomisationConfiguration = z.infer<
  typeof MainHeaderComponentCustomisationConfigurationSchema
>

export const ActivitiesComponentCustomisationConfigurationSchema = z.object({
  showActivityIcons: z.boolean(),
  showCountryFlags: z.boolean(),
  showCertificates: z.boolean(),
  activitiesDepth: z.number(),
})
export type ActivitiesComponentCustomisationConfiguration = z.infer<
  typeof ActivitiesComponentCustomisationConfigurationSchema
>

export const LogoConfigurationSchema = z.object({
  logoFile: FormFileSchema.nullable(),
})
export type LogoConfiguration = z.infer<typeof LogoConfigurationSchema>

export const ComponentCustomisationConfigurationMapper = {
  [CUSTOMISATION_CATEGORIES.FOOTER]:
    FooterComponentCustomisationConfigurationSchema,
  [CUSTOMISATION_CATEGORIES.MAIN_HEADER]:
    MainHeaderComponentCustomisationConfigurationSchema,
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesComponentCustomisationConfigurationSchema,
}

export const ComponentCustomisationConfigurationSchema = z.object({
  [CUSTOMISATION_CATEGORIES.FOOTER]:
    FooterComponentCustomisationConfigurationSchema,
  [CUSTOMISATION_CATEGORIES.MAIN_HEADER]:
    MainHeaderComponentCustomisationConfigurationSchema,
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesComponentCustomisationConfigurationSchema,
})
export type ComponentCustomisationConfiguration = z.infer<
  typeof ComponentCustomisationConfigurationSchema
>

export const IndividualConfigurationSchema = z.union([
  FooterComponentCustomisationConfigurationSchema,
  MainHeaderComponentCustomisationConfigurationSchema,
  ActivitiesComponentCustomisationConfigurationSchema,
])
export type IndividualConfiguration = z.infer<
  typeof IndividualConfigurationSchema
>

export const ConfigurationsSchema = z.object({
  [CUSTOMISATION_CATEGORIES.FOOTER]:
    FooterComponentCustomisationConfigurationSchema,
  [CUSTOMISATION_CATEGORIES.MAIN_HEADER]:
    MainHeaderComponentCustomisationConfigurationSchema,
  [CUSTOMISATION_CATEGORIES.ACTIVITIES]:
    ActivitiesComponentCustomisationConfigurationSchema,
})
export type Configurations = z.infer<typeof ConfigurationsSchema>

export const CONFIGURATIONS_SETTINGS_SCOPE = {
  COMPONENT_CUSTOMISATION: SETTINGS_SCOPE.COMPONENT_CUSTOMISATION,
  CONTENT_FILTERING: SETTINGS_SCOPE.CONTENT_FILTERING,
} as const
export type ConfigurationsSettingsScope =
  (typeof CONFIGURATIONS_SETTINGS_SCOPE)[keyof typeof CONFIGURATIONS_SETTINGS_SCOPE]

export const CategorisedConfigurationsSchema = z.object({
  name: z.literal(SETTINGS_SCOPE.COMPONENT_CUSTOMISATION),
  type: z.literal(ConfigurationsScopeType.CATEGORISED),
  categories: z.record(z.string(), z.any()),
})
export type CategorisedConfigurations = z.infer<
  typeof CategorisedConfigurationsSchema
>

const FlatConfigurationsSchema = z.object({
  name: z.literal(SETTINGS_SCOPE.CONTENT_FILTERING),
  type: z.literal(ConfigurationsScopeType.FLAT),
  props: z.record(z.unknown()),
})
export type FlatConfigurations = z.infer<typeof FlatConfigurationsSchema>

export const ProductAttributesSchema = z.nativeEnum(PRODUCT_ATTRIBUTES)

export const ContentFilteringPropsSchema = z.object({
  showTraceabilityDetails: z.boolean(),
  standardsToBeShown: z.array(z.number()),
  logoFile: FormFileSchema.optional(),
  primaryProductAttributes: z.array(ProductAttributesSchema),
  secondaryProductAttributes: z.array(ProductAttributesSchema),
})

export const ContentFilteringConfigurationsSchema = z.object({
  name: z.literal(SETTINGS_SCOPE.CONTENT_FILTERING),
  type: z.literal(ConfigurationsScopeType.FLAT),
  props: ContentFilteringPropsSchema,
})

export type ContentFilteringProps = z.infer<typeof ContentFilteringPropsSchema>
export type ContentFilteringConfigurations = z.infer<
  typeof ContentFilteringConfigurationsSchema
>

export const ConfigurationsUnionSchema = z.union([
  CategorisedConfigurationsSchema,
  FlatConfigurationsSchema,
])
